export type TransactionInputField = {
  name: string;
  maxLength: number;
};

export const splitByMaxLengths = (value: string, maxLengths: readonly number[]): string[] => {
  const parts: string[] = [];
  let cursor = 0;

  for (const maxLength of maxLengths) {
    parts.push(value.slice(cursor, cursor + maxLength));
    cursor += maxLength;
  }

  return parts;
};

export const totalMaxLength = (fields: readonly TransactionInputField[]): number =>
  fields.reduce((sum, field) => sum + field.maxLength, 0);

const valuesFromParts = (
  fields: readonly TransactionInputField[],
  parts: readonly string[]
): Record<string, string> =>
  Object.fromEntries(fields.map((field, index) => [field.name, parts[index] ?? '']));

export const nextTransactionInputValues = ({
  fields,
  current,
  inputName,
  nextValue,
  source,
}: {
  fields: readonly TransactionInputField[];
  current: Record<string, string>;
  inputName: string;
  nextValue: string;
  source: 'paste' | 'change';
}): Record<string, string> | null => {
  const field = fields.find(({ name }) => name === inputName);

  if (!field) {
    return current;
  }

  const combinedMax = totalMaxLength(fields);

  if (nextValue.length > combinedMax) {
    return null;
  }

  const previousValue = current[inputName] ?? '';
  const isBulkChange = nextValue.length - previousValue.length > 1;
  const shouldFillInOrder = source === 'paste' || isBulkChange;

  if (shouldFillInOrder) {
    return valuesFromParts(
      fields,
      splitByMaxLengths(
        nextValue,
        fields.map(({ maxLength }) => maxLength)
      )
    );
  }

  if (nextValue.length > field.maxLength) {
    return null;
  }

  return { ...current, [inputName]: nextValue };
};
